// @flow
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Done as DoneIcon,
  DoneAll as DoneAllIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  Receipt as BillingIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Person as BadgeIcon,
  Home as AddressIcon
} from '@material-ui/icons';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { toSentence } from '../util/string';
import { Formik } from 'formik';
import { isNotValidEmail, isNotValidName } from '../util/validator';
import { AuthContext } from '../context/auth';

const useStyles = makeStyles((theme) => ({
  draft: {
    color: theme.palette.secondary[ '900' ],
  },
  grey: {
    color: theme.palette.text.secondary,
  },
  error: {
    color: theme.palette.error.main,
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  form: {
    width: '100%', // Fix IE 11 issue.
  },
  sectionTitle: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  sectionPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  statusChip: {
    marginLeft: 'auto',
    fontWeight: 500,
  },
  errorText: {
    fontSize: '0.75rem',
    marginTop: 3,
    marginLeft: 14,
    color: theme.palette.error.main,
  },
  accordionSummary: {
    '& .MuiAccordionSummary-content': {
      alignItems: 'center',
      gap: theme.spacing(2),
    },
  },
}));

const ProcessBillingData = (props) => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const classes = useStyles();
  const { code, process, expanded, disabled, onExpanded, onSave, renewal = false } = props;

  const validate = values => {
    if (!!values.billToFinalCustomer) {
      return {};
    }
    const errors = {};
    if (!values.billingName) {
      errors.billingName = t('Required');
    } else if (isNotValidName(values.billingName)) {
      errors.billingName = t('Invalid name');
    }
    if (!values.billingEmail) {
      errors.billingEmail = t('Required');
    } else if (isNotValidEmail(values.billingEmail)) {
      errors.billingEmail = t('Invalid email address');
    }
    if (!values.billingLegalIDType) {
      errors.billingLegalIDType = t('Required');
    }
    if (!values.billingLegalID) {
      errors.billingLegalID = t('Required');
    }
    if (!values.billingAddress) {
      errors.billingAddress = t('Required');
    }
    return errors;
  };

  const onSubmit = async (values, { setSubmitting }) => {
    onSave(values);
    setSubmitting(false);
  };

  let processStatusText = process.hasBilling() ? 'Completed' : 'Not completed';
  let processStatusIcon = process.hasBilling() ? <DoneIcon /> : <EditIcon />;
  let statusColor = process.hasBilling() ? 'primary' : 'default';
  
  if (process.hasBillingValidated()) {
    processStatusText = 'Validated';
    processStatusIcon = <DoneAllIcon />;
    statusColor = 'success';
  }

  const userData = !!user ? user : {};

  // Concatenate process.name and process.lastName if both exist
  const processFullName = process.name && process.lastName 
    ? `${process.name} ${process.lastName}`.trim()
    : process.name || '';
  
  return (
    <Formik
      initialValues={{
        sameAsProcess: process.hasSameBillingAndData(),
        billToFinalCustomer: !!process.billToFinalCustomer,
        billingName: process.billingName || userData.billingName || processFullName,
        billingEmail: process.billingEmail || userData.billingEmail || process.email,
        billingLegalIDType: process.billingLegalIDType || userData.billingLegalIDType || process.legalIDType,
        billingLegalID: process.billingLegalID || userData.billingLegalID || process.legalID,
        billingAddress: process.billingAddress || userData.billingAddress || process.addressOfResidence,
      }}
      validate={validate}
      onSubmit={onSubmit}
    >
      {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
        }) => (
        <form className={classes.form} onSubmit={handleSubmit}>
          <Accordion
            key={code}
            expanded={expanded}
            onChange={onExpanded(code)}
            TransitionProps={{ unmountOnExit: true }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon/>}
              aria-controls={`${code}-panel`}
              id={`${code}-panel`}
              className={classes.accordionSummary}
            >
              <Typography className={classes.heading} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <BillingIcon /> {t('Billing data')}
              </Typography>
              <Chip
                icon={processStatusIcon}
                label={t(processStatusText)}
                color={statusColor}
                size="small"
                className={classes.statusChip}
              />
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {!values.billToFinalCustomer && !renewal && (
                  <Grid item xs={12} md={6}>
                    <Paper className={classes.sectionPaper} elevation={0}>
                      <FormControlLabel
                        control={<Checkbox
                          disabled={disabled}
                          checked={values.sameAsProcess}
                          onChange={handleChange}
                          name="sameAsProcess"
                          color="primary"/>}
                        label={<Typography variant="subtitle2">
                          <strong>{t('My personal data')}</strong>
                        </Typography>}/>
                    </Paper>
                  </Grid>
                )}

                {!values.sameAsProcess && (
                  <Grid item xs={12} md={6}>
                    <Paper className={classes.sectionPaper} elevation={0}>
                      <FormControlLabel
                        control={<Checkbox
                          disabled={disabled}
                          checked={values.billToFinalCustomer}
                          onChange={handleChange}
                          name="billToFinalCustomer"
                          color="primary"/>}
                        label={<Typography variant="subtitle2">
                          <strong>{t('Final customer')}</strong>
                        </Typography>}/>
                    </Paper>
                  </Grid>
                )}

                {(!values.sameAsProcess && !values.billToFinalCustomer) && (
                  <>
                    <Grid item xs={12}>
                      <Divider style={{ margin: '16px 0' }} />
                      <Typography variant="h6" className={classes.sectionTitle}>
                        <PersonIcon /> {t('Billing Information')}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        disabled={disabled || values.billToFinalCustomer}
                        variant="outlined"
                        fullWidth
                        required
                        id="billingName"
                        label={t('Name')}
                        name="billingName"
                        autoComplete="billingName"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.billingName}
                        error={errors.billingName && touched.billingName}
                        helperText={errors.billingName && touched.billingName && errors.billingName}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        disabled={disabled || values.billToFinalCustomer}
                        variant="outlined"
                        fullWidth
                        required
                        type="email"
                        id="billingEmail"
                        label={t('Email')}
                        name="billingEmail"
                        autoComplete="billingEmail"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.billingEmail}
                        error={errors.billingEmail && touched.billingEmail}
                        helperText={errors.billingEmail && touched.billingEmail && errors.billingEmail}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <FormControl
                        fullWidth
                        variant="outlined"
                        disabled={disabled || values.billToFinalCustomer}
                        error={errors.billingLegalIDType && touched.billingLegalIDType}
                      >
                        <InputLabel id="legalIDTypeLabel">
                          {t('Legal ID type')}
                        </InputLabel>
                        <Select
                          labelId="legalIDTypeLabel"
                          label={t('Legal ID type')}
                          id="billingLegalIDType"
                          name="billingLegalIDType"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.billingLegalIDType}
                          startAdornment={
                            <InputAdornment position="start">
                              <BadgeIcon color="action" />
                            </InputAdornment>
                          }
                        >
                          {['EC_RUC', "PASSPORT", "EC_CEDULA"].map((item) => (
                            <MenuItem key={item} value={item}>
                              {t(toSentence(item))}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.billingLegalIDType && touched.billingLegalIDType && (
                          <Typography className={classes.errorText}>
                            {errors.billingLegalIDType}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        disabled={disabled || values.billToFinalCustomer}
                        variant="outlined"
                        fullWidth
                        required
                        id="billingLegalID"
                        label={t('Legal ID')}
                        name="billingLegalID"
                        autoComplete="billingLegalID"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.billingLegalID}
                        error={errors.billingLegalID && touched.billingLegalID}
                        helperText={errors.billingLegalID && touched.billingLegalID && errors.billingLegalID}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <BadgeIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={8}>
                      <TextField
                        disabled={disabled || values.billToFinalCustomer}
                        variant="outlined"
                        fullWidth
                        required
                        id="billingAddress"
                        label={t('Address')}
                        name="billingAddress"
                        autoComplete="billingAddress"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.billingAddress}
                        error={errors.billingAddress && touched.billingAddress}
                        helperText={errors.billingAddress && touched.billingAddress && errors.billingAddress}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AddressIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </AccordionDetails>
            {<>
              <Divider/>
              <AccordionActions>
                <Button
                  type="submit"
                  disabled={disabled}
                  variant="contained"
                  color="primary"
                  startIcon={disabled ? <CircularProgress color="secondary" size="1.2em"/> : null}
                >
                  {t('Save')}
                </Button>
              </AccordionActions>
            </>}
          </Accordion>
        </form>
      )}
    </Formik>
  );
};

export default ProcessBillingData;
